import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { ClientId, RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api';
import {
	InlineInputBoxAbortMessageCommand,
	RovoStreamAbortedCommand,
	ROVO_CHAT_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands';

import { fg } from '@confluence/feature-gating';

import { log } from '../utils/aifc-logging';
import type { ContentContext } from '../bridge-management/types/command-context-types';

import {
	FinishAction,
	InitAction,
	PartialStreamParser,
	StreamAction,
} from './partial-stream-parser';
import type { EditorAIAnalyticEventPayload } from '@atlassian/editor-plugin-ai';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';

const SKIP_INFINITE_LOOP_ACTIONS = ['insertNodeAfter', 'insertNodeBefore'];

class ToolCallProcessor {
	private actionId: string;
	private accumulated: string = '';

	constructor(
		private editorApi: PublicPluginAPI<AiStreamingOrchestratorPlugin>,
		private contentContext: ContentContext,
		command: StreamingCommand,
	) {
		const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(
			command,
			fg('platform_editor_ai_aifc_patch_beta')
				? {
						/**
						 * Always hide the loading screen as staging area always shows Rovo conversation
						 * and will already have a loading indicator.
						 */
						hideLoadingScreen: this.contentContext === 'staging-area',
					}
				: undefined,
		);
		if (!actionId) {
			throw new Error('Failed to initialize streaming action for update');
		}
		this.actionId = actionId;
		log('Initialized editor streaming action', { streamType: 'update', actionId, command });
	}

	stream(adf: string) {
		if (!adf) {
			return;
		}

		this.accumulated += adf;
		this.editorApi?.aiStreamingOrchestrator?.actions.streamValue(this.actionId, adf);
		log('Streaming content', { actionId: this.actionId, adf });
	}

	finish() {
		this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
		log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });
		this.accumulated = '';
	}
}

export class ConfluencePageUpdateCommandProcessor {
	private parser: PartialStreamParser;
	private currentToolCall: ToolCallProcessor | undefined;
	private accumulated: string = '';
	private lastAction: string | null;
	private hasDetectedInfiniteLoop: boolean = false;

	constructor(
		private editorApi: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
		private contentContext: ContentContext,
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
	) {
		this.parser = new PartialStreamParser();
		this.lastAction = null;
	}

	private createToolCallProcessor(toolCall: InitAction) {
		const type = toolCall.name;
		const localId = toolCall.localId;

		let command: StreamingCommand;
		switch (type) {
			case 'replaceNode':
			case 'insertNodeBefore':
			case 'insertNodeAfter':
			case 'removeNode':
				command = {
					type,
					localId: localId!,
				};
				break;
			case 'appendNode':
				command = {
					type,
				};
				break;
			case 'wrap':
				const wrapperType = toolCall.wrapperType;
				const wrapperAttrs = toolCall.wrapperAttrs;
				if (!wrapperType || !wrapperAttrs) {
					return;
				}

				command = {
					type,
					localId: localId!,
					wrapperType,
					wrapperAttrs,
				};
				break;
			default:
				return;
		}

		return new ToolCallProcessor(this.editorApi, this.contentContext, command);
	}

	streamChunk(chunk: string, isFinished: boolean) {
		// The last message contains a copy of the full, accumulated tool call structure. We
		// don't want to reprocess this, so we just return instead.
		if (isFinished) {
			return;
		}

		if (this.accumulated.length === 0) {
			this.hasDetectedInfiniteLoop = false;
			this.rovoBridgeAPI.off(this.clientId, RovoStreamAbortedCommand);
			this.editorApi.aiStreamingOrchestrator?.actions.startSession();
		}

		this.accumulated += chunk;
		return this.stream(chunk);
	}

	private async stream(chunk: string) {
		// Once we detect an infinite loop and call finish(), we won't be able to recover
		if (this.hasDetectedInfiniteLoop) {
			return;
		}
		const results = this.parser.parse(chunk);

		for (const result of results) {
			if (result instanceof InitAction) {
				if (result.localId) {
					if (
						this.lastAction === `${result.name}-${result.localId}` &&
						!SKIP_INFINITE_LOOP_ACTIONS.includes(result.name) &&
						fg('platform_editor_ai_infinite_loop_detection')
					) {
						// Once we detect an infinite loop, we keep skipping and wait for the streaming to finish.
						this.hasDetectedInfiniteLoop = true;
						this.rovoBridgeAPI.on(this.clientId, RovoStreamAbortedCommand, () => {
							this.hasDetectedInfiniteLoop = false;
							this.rovoBridgeAPI.off(this.clientId, RovoStreamAbortedCommand);
						});
						void this.finish();
						this.editorApi.analytics?.actions.fireAnalyticsEvent<
							EditorAIAnalyticEventPayload,
							'customEventType'
						>({
							action: 'error',
							actionSubject: 'editorPluginAI',
							actionSubjectId: 'adfStreaming',
							eventType: EVENT_TYPE.OPERATIONAL,
							attributes: {
								errorMessage: 'Infinite loop detected',
								errorData: {
									action: result.name,
									localId: result.localId,
								},
								experienceName: null,
								selectionType: null,
								selectionCategory: null,
								streamId: null,
								source: { type: 'orchestrator', isCreateAction: false },
							},
						});
					} else {
						this.hasDetectedInfiniteLoop = false;
						this.currentToolCall = this.createToolCallProcessor(result);
					}
					this.lastAction = `${result.name}-${result.localId}`;
				} else {
					this.lastAction = null;
					this.currentToolCall = this.createToolCallProcessor(result);
				}
			} else if (result instanceof StreamAction) {
				this.currentToolCall?.stream(result.partialValue);
			} else if (result instanceof FinishAction) {
				this.currentToolCall?.finish();
			} else {
				throw new Error('Unknown action type from partial stream parser');
			}
		}
	}

	async finish() {
		this.accumulated = '';
		await this.editorApi.aiStreamingOrchestrator?.actions.endSession();

		if (this.hasDetectedInfiniteLoop) {
			this.rovoBridgeAPI.send(InlineInputBoxAbortMessageCommand, {
				receiverId: ROVO_CHAT_CLIENT_ID,
				senderId: this.clientId,
			});
		}

		this.currentToolCall = undefined;
		this.parser.reset();
	}

	abort() {
		this.editorApi.aiStreamingOrchestrator?.actions.abortSession();
	}
}
